import { ThemedText } from "@/components/themed-text";
import {
  formatVehicleLabel,
  getVehicleTypeShortLabel,
} from "@/components/vehicle/vehicle-card-utils";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { Booking } from "@/lib/services/bookings";
import { getRemoteImageSource } from "@/lib/utils/media-url";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Image } from "expo-image";
import React, { useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";

interface BookingVehicleInfoProps {
  booking: Booking;
  embedded?: boolean;
}

interface VehicleDetailRow {
  label: string;
  value: string;
}

function formatWeight(weight?: string): string | null {
  if (!weight) {
    return null;
  }

  const trimmed = weight.trim();
  if (/kg/i.test(trimmed)) {
    return trimmed;
  }

  return `${trimmed} kg`;
}

function buildVehicleDetails(booking: Booking): VehicleDetailRow[] {
  const rows: (VehicleDetailRow | null)[] = [
    booking.vehicle_registration
      ? {
          label: "Registration",
          value: booking.vehicle_registration.toUpperCase(),
        }
      : null,
    booking.vehicle_make
      ? {
          label: "Vehicle make",
          value: formatVehicleLabel(booking.vehicle_make),
        }
      : null,
    booking.vehicle_model
      ? {
          label: "Vehicle model",
          value: formatVehicleLabel(booking.vehicle_model),
        }
      : null,
    booking.vehicle_colour
      ? { label: "Colour", value: formatVehicleLabel(booking.vehicle_colour) }
      : null,
    booking.vehicle_fuel_type
      ? {
          label: "Fuel type",
          value: formatVehicleLabel(booking.vehicle_fuel_type),
        }
      : null,
    booking.vehicle_year
      ? { label: "Year", value: String(booking.vehicle_year) }
      : null,
    booking.vehicle_body_type
      ? {
          label: "Body type",
          value: formatVehicleLabel(booking.vehicle_body_type),
        }
      : null,
    booking.vehicle_category
      ? {
          label: "Category",
          value: getVehicleTypeShortLabel(booking.vehicle_category),
        }
      : null,
    formatWeight(booking.vehicle_weight)
      ? { label: "Weight", value: formatWeight(booking.vehicle_weight)! }
      : null,
  ];

  return rows.filter((row): row is VehicleDetailRow => row !== null);
}

export const BookingVehicleInfo: React.FC<BookingVehicleInfoProps> = ({
  booking,
  embedded = false,
}) => {
  const { theme } = useAuth();
  const vehicleDetails = buildVehicleDetails(booking);
  const [imageError, setImageError] = useState(false);
  const imageSource = useMemo(
    () => getRemoteImageSource(booking.vehicle_image_path),
    [booking.vehicle_image_path],
  );
  const showVehicleImage = imageSource !== null && !imageError;

  return (
    <View style={[styles.section, embedded && styles.sectionEmbedded]}>
      {!embedded ? (
        <ThemedText style={styles.sectionTitle} type="defaultSemiBold">
          Vehicle information
        </ThemedText>
      ) : null}

      <View
        style={[
          styles.card,
          {
            backgroundColor: theme.card,
            borderColor: theme.border,
            shadowColor: theme.background,
          },
        ]}
      >
        {showVehicleImage ? (
          <Image
            source={imageSource}
            style={styles.vehicleImage}
            contentFit="cover"
            transition={200}
            recyclingKey={imageSource.uri}
            onError={() => setImageError(true)}
          />
        ) : (
          <View style={[styles.vehicleImage, styles.noImageContainer]}>
            <Ionicons
              name="car-outline"
              size={40}
              color={BrandColors.mediumGray}
            />
            <ThemedText style={styles.noImageText} type="extraSmall">
              No vehicle photo
            </ThemedText>
          </View>
        )}

        {vehicleDetails.length > 0 ? (
          <View style={styles.detailsList}>
            {vehicleDetails.map((detail, index) => (
              <View
                key={detail.label}
                style={[
                  styles.detailRow,
                  index < vehicleDetails.length - 1 && styles.detailRowBorder,
                  { borderBottomColor: theme.border },
                ]}
              >
                <ThemedText style={styles.detailLabel} type="small">
                  {detail.label}
                </ThemedText>
                <ThemedText
                  style={styles.detailValue}
                  type="smallSemiBold"
                  numberOfLines={2}
                >
                  {detail.value}
                </ThemedText>
              </View>
            ))}
          </View>
        ) : (
          <ThemedText style={styles.emptyText} type="small">
            No vehicle details available.
          </ThemedText>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    marginBottom: 8,
  },
  sectionEmbedded: {
    marginBottom: 0,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "700",
    marginBottom: 12,
  },
  card: {
    borderRadius: 16,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
    borderWidth: 1,
  },
  vehicleImage: {
    width: "100%",
    height: 160,
    backgroundColor: BrandColors.backgroundGray,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  noImageContainer: {
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  noImageText: {
    color: BrandColors.mediumGray,
  },
  detailsList: {
    paddingHorizontal: 14,
    paddingVertical: 4,
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingVertical: 12,
  },
  detailRowBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  detailLabel: {
    flex: 1,
    color: BrandColors.mediumGray,
  },
  detailValue: {
    flex: 1,
    textAlign: "right",
    textTransform: "uppercase",
  },
  emptyText: {
    color: BrandColors.mediumGray,
    padding: 14,
  },
});
